import type { Storage } from '../../Storage.js'
import { legacy, storageKey } from './legacy.js'

const wrappers = new WeakMap<Storage, Storage>()
const compatibleStorages = new WeakSet<Storage>()
const normalizers = new WeakMap<Storage, (value: unknown) => unknown>()
const operationScopes = new WeakMap<Storage, object>()
const operationQueues = new WeakMap<object, Map<string, Promise<void>>>()

export class StorageMigrationError extends Error {
  override name = 'StorageMigrationError'

  constructor(cause?: unknown) {
    super('Historical browser state migration failed.', { cause })
  }
}

export function withLegacyFallback(storage: Storage): Storage {
  if (compatibleStorages.has(storage)) return storage
  const existing = wrappers.get(storage)
  if (existing) return existing

  const compatible = {
    getItem<value>(name: string) {
      return enqueue(storage, name, async () => {
        const alias = storageKey(name)
        const current = await readOrThrow<value>(storage, name)
        if (current.status === 'found') {
          const canonical = translate(current.value) as value
          assertValid(name, canonical)
          if (!storageValuesEqual(current.value, canonical))
            await writeAndVerify(storage, name, canonical)
          if (alias) await removeAndVerify(storage, alias)
          return canonical
        }

        if (!alias) return null
        const previous = await readOrThrow<value>(storage, alias)
        if (previous.status !== 'found') return null

        const migrated = translate(previous.value) as value
        assertValid(name, migrated)
        await writeAndVerify(storage, name, migrated)
        await removeAndVerify(storage, alias)
        return migrated
      })
    },
    removeItem(name: string) {
      return enqueue(storage, name, async () => {
        const alias = storageKey(name)
        await removeAndVerify(storage, name)
        if (alias) await removeAndVerify(storage, alias)
      })
    },
    setItem(name: string, value: unknown) {
      return enqueue(storage, name, async () => {
        const alias = storageKey(name)
        const canonical = translate(value)
        assertValid(name, canonical)
        await writeAndVerify(storage, name, canonical)
        if (alias) await removeAndVerify(storage, alias)
      })
    },
    sizeLimit: storage.sizeLimit,
    storages: storage.storages,
  } satisfies Storage

  compatibleStorages.add(compatible)
  inheritStorageNormalizer(compatible, storage)
  inheritStorageOperationScope(compatible, storage)
  wrappers.set(storage, compatible)
  wrappers.set(compatible, compatible)
  return compatible
}

export function inheritStorageCompatibility(
  storage: Storage,
  ...sources: readonly Storage[]
) {
  if (
    sources.length > 0 &&
    sources.every((source) => compatibleStorages.has(source))
  )
    compatibleStorages.add(storage)
  return storage
}

export function withStorageNormalizer(
  storage: Storage,
  normalize: (value: unknown) => unknown,
) {
  normalizers.set(storage, normalize)
  return storage
}

export function inheritStorageNormalizer(
  storage: Storage,
  ...sources: readonly Storage[]
) {
  const normalize = sources[0] ? normalizers.get(sources[0]) : undefined
  if (
    normalize &&
    sources.every((source) => normalizers.get(source) === normalize)
  )
    normalizers.set(storage, normalize)
  return storage
}

export function withStorageOperationScope(storage: Storage, scope: object) {
  operationScopes.set(storage, scope)
  return storage
}

export function inheritStorageOperationScope(
  storage: Storage,
  source: Storage,
) {
  const scope = operationScopes.get(source)
  if (scope) operationScopes.set(storage, scope)
  return storage
}

export function storageValuesEqualFor(
  storage: Storage,
  written: unknown,
  expected: unknown,
) {
  const normalize = normalizers.get(storage)
  return storageValuesEqual(written, normalize ? normalize(expected) : expected)
}

function enqueue<value>(
  storage: Storage,
  name: string,
  operation: () => Promise<value>,
) {
  const scope = operationScopes.get(storage) ?? storage
  let queues = operationQueues.get(scope)
  if (!queues) {
    queues = new Map()
    operationQueues.set(scope, queues)
  }

  const previous = queues.get(name) ?? Promise.resolve()
  const current = previous.catch(() => {}).then(operation)
  const settled = current.then(
    () => {},
    () => {},
  )
  queues.set(name, settled)
  void settled.then(() => {
    if (queues?.get(name) === settled) queues.delete(name)
  })
  return current
}

/** Canonical-first storage with read-repair from a historical backing store. */
export function withStorageFallback(
  canonical: Storage,
  historical: Storage,
): Storage {
  return {
    async getItem<value>(name: string) {
      const current = await readOrThrow<value>(canonical, name)
      if (current.status === 'found') {
        assertValid(name, current.value)
        await removeAndVerify(historical, name)
        return current.value
      }

      const previous = await readOrThrow<value>(historical, name)
      if (previous.status === 'found') {
        assertValid(name, previous.value)
        await writeAndVerify(canonical, name, previous.value)
        await removeAndVerify(historical, name)
        return previous.value
      }

      return null
    },
    async removeItem(name) {
      await removeAndVerify(canonical, name)
      await removeAndVerify(historical, name)
    },
    async setItem(name, value) {
      assertValid(name, value)
      await writeAndVerify(canonical, name, value)
      await removeAndVerify(historical, name)
    },
    sizeLimit: Math.min(canonical.sizeLimit, historical.sizeLimit),
    storages: [canonical, historical],
  }
}

type ReadResult<value> =
  | { status: 'failed'; error: unknown }
  | { status: 'found'; value: value }
  | { status: 'missing' }

async function read<value>(
  storage: Storage,
  name: string,
): Promise<ReadResult<value>> {
  try {
    const value = await storage.getItem<value>(name)
    return value == null ? { status: 'missing' } : { status: 'found', value }
  } catch (error) {
    return { error, status: 'failed' }
  }
}

async function readOrThrow<value>(storage: Storage, name: string) {
  const result = await read<value>(storage, name)
  if (result.status === 'failed') throw new StorageMigrationError(result.error)
  return result
}

async function writeAndVerify(storage: Storage, name: string, value: unknown) {
  try {
    await storage.setItem(name, value)
  } catch (error) {
    throw new StorageMigrationError(error)
  }

  const written = await readOrThrow(storage, name)
  if (
    written.status !== 'found' ||
    !storageValuesEqualFor(storage, written.value, value)
  )
    throw new StorageMigrationError()
}

async function removeAndVerify(storage: Storage, name: string) {
  const existing = await readOrThrow(storage, name)
  if (existing.status === 'missing') return

  try {
    await storage.removeItem(name)
  } catch (error) {
    throw new StorageMigrationError(error)
  }

  const removed = await readOrThrow(storage, name)
  if (removed.status !== 'missing') throw new StorageMigrationError()
}

export function canonicalizeStorageValue(value: unknown): unknown {
  return translate(value)
}

function translate(value: unknown): unknown {
  try {
    if (!isPlainObject(value)) return value
    const state = value.state
    if (!isPlainObject(state) || !Array.isArray(state.accounts)) return value

    let changed = false
    const accounts = state.accounts.map((account) => {
      if (!isPlainObject(account)) return account
      const source = translateAccountSource(account.source)
      if (source === account.source) return account
      changed = true
      return { ...account, source }
    })

    if (!changed) return value
    return { ...value, state: { ...state, accounts } }
  } catch {
    return value
  }
}

export function assertValidStorageValue(name: string, value: unknown) {
  assertValid(name, value)
}

function assertValid(name: string, value: unknown) {
  const valid = (() => {
    if (name.startsWith('levery.store'))
      return (
        isPlainObject(value) &&
        isPlainObject(value.state) &&
        Array.isArray(value.state.accounts)
      )
    if (name === 'levery.dialog')
      return isPlainObject(value) && isPlainObject(value.state)
    if (name === 'levery.authUrl')
      return (
        isPlainObject(value) &&
        typeof value.logout === 'string' &&
        typeof value.nonce === 'string' &&
        typeof value.verify === 'string'
      )
    if (name === '__levery_theme') return typeof value === 'string'
    if (name.startsWith('levery.webauthnVerified.'))
      return typeof value === 'number' && Number.isFinite(value) && value >= 0
    return true
  })()

  if (!valid) throw new StorageMigrationError()
}

export function storageValuesEqual(left: unknown, right: unknown) {
  return equal(left, right, new WeakMap())
}

function equal(
  left: unknown,
  right: unknown,
  seen: WeakMap<object, object>,
): boolean {
  if (Object.is(left, right)) return true
  if (!left || !right || typeof left !== 'object' || typeof right !== 'object')
    return false

  if (seen.get(left) === right) return true
  seen.set(left, right)

  if (left instanceof Date || right instanceof Date)
    return (
      left instanceof Date &&
      right instanceof Date &&
      left.getTime() === right.getTime()
    )
  if (left instanceof Uint8Array || right instanceof Uint8Array)
    return (
      left instanceof Uint8Array &&
      right instanceof Uint8Array &&
      left.length === right.length &&
      left.every((value, index) => value === right[index])
    )
  if (left instanceof Map || right instanceof Map) {
    if (!(left instanceof Map) || !(right instanceof Map)) return false
    if (left.size !== right.size) return false
    const leftEntries = [...left.entries()]
    const rightEntries = [...right.entries()]
    return leftEntries.every(([key, value], index) => {
      const entry = rightEntries[index]
      return (
        !!entry && equal(key, entry[0], seen) && equal(value, entry[1], seen)
      )
    })
  }
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right)) return false
    return (
      left.length === right.length &&
      left.every((value, index) => equal(value, right[index], seen))
    )
  }

  const leftRecord = left as Record<string, unknown>
  const rightRecord = right as Record<string, unknown>
  const leftKeys = Object.keys(leftRecord).filter(
    (key) => leftRecord[key] !== undefined,
  )
  const rightKeys = Object.keys(rightRecord).filter(
    (key) => rightRecord[key] !== undefined,
  )
  return (
    leftKeys.length === rightKeys.length &&
    leftKeys.every(
      (key) =>
        Object.hasOwn(right, key) &&
        equal(leftRecord[key], rightRecord[key], seen),
    )
  )
}

function translateAccountSource(value: unknown) {
  if (value === legacy.accountSource) return 'levery'
  return value
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object') return false
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}
